/**
 * The platform as one collapsible, numbered outline — every section, page and
 * sub-page, each a link. Rendered on `/help/structure`.
 *
 * Presentation only: the tree, the filter and the counts all come from
 * `WbsTree.transform.ts`. This file owns which branches are open and what is
 * typed in the filter box.
 *
 * @see src/versions/v1/modules/help/WbsTree.transform.ts
 * @see docs/logic/help/structure.md
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ChevronsDownUp, ChevronsUpDown, Search, X } from 'lucide-react';
import { Input } from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import {
  branchIds,
  descendantCount,
  filterWbs,
  highlightParts,
  type WbsNode,
} from './WbsTree.transform';

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]';

export function WbsTree({ nodes }: { nodes: readonly WbsNode[] }) {
  // First paint: the sections open, so the reader sees the second level —
  // the pages — without anything deeper.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(
    () => new Set(nodes.map((n) => n.id)),
  );
  const [query, setQuery] = useState('');

  const allBranches = useMemo(() => branchIds(nodes), [nodes]);
  const filtered = useMemo(() => filterWbs(nodes, query), [nodes, query]);
  const filtering = query.trim().length > 0;
  const open = filtering ? filtered.openIds : expanded;

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Commentable name="WbsTree">
      <section
        aria-label="Platform structure"
        className="rounded-xl border"
        style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
      >
        <div
          className="flex flex-wrap items-center gap-2 border-b px-3 py-2.5"
          style={{ borderColor: 'var(--hairline)' }}
        >
          <label className="relative min-w-[220px] flex-1 @3xl:max-w-[360px]">
            <span className="sr-only">Filter the structure</span>
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2"
              style={{ color: 'var(--ink-tertiary)' }}
              aria-hidden="true"
            />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setQuery('');
              }}
              placeholder="Filter pages — try “stock” or “3.07”"
              className="w-full [&::-webkit-search-cancel-button]:hidden"
              style={{ paddingLeft: 30, paddingRight: 28, width: '100%', background: 'var(--surface-1)' }}
            />
            {filtering ? (
              <button
                type="button"
                aria-label="Clear the filter"
                onClick={() => setQuery('')}
                className={cn(
                  'absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded hover:bg-[var(--surface-2)]',
                  FOCUS_RING,
                )}
                style={{ color: 'var(--ink-tertiary)' }}
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            ) : null}
          </label>

          <div className="flex items-center gap-0.5">
            <button
              type="button"
              className={cn('btn-ghost disabled:cursor-default disabled:opacity-40', FOCUS_RING)}
              disabled={filtering}
              onClick={() => setExpanded(new Set(allBranches))}
            >
              <ChevronsUpDown className="h-3.5 w-3.5" aria-hidden="true" />
              Expand all
            </button>
            <button
              type="button"
              className={cn('btn-ghost disabled:cursor-default disabled:opacity-40', FOCUS_RING)}
              disabled={filtering}
              onClick={() => setExpanded(new Set())}
            >
              <ChevronsDownUp className="h-3.5 w-3.5" aria-hidden="true" />
              Collapse all
            </button>
          </div>

          <div
            className="ml-auto flex items-center gap-3 text-[11px]"
            style={{ color: 'var(--ink-tertiary)' }}
          >
            {filtering ? (
              <span aria-live="polite">
                {filtered.matchCount === 0
                  ? 'No matches'
                  : `${filtered.matchCount} match${filtered.matchCount === 1 ? '' : 'es'}`}
              </span>
            ) : null}
            <span className="flex items-center gap-1.5">
              <DeepDiveTag />
              <span>not on the executive rail — one drill-down away</span>
            </span>
          </div>
        </div>

        {filtered.nodes.length === 0 ? (
          <p className="px-4 py-10 text-center text-[12.5px]" style={{ color: 'var(--ink-tertiary)' }}>
            Nothing in the platform matches “{query.trim()}”.
          </p>
        ) : (
          <ul className="px-2 py-2">
            {filtered.nodes.map((node) => (
              <WbsRow
                key={node.id}
                node={node}
                depth={0}
                open={open}
                onToggle={toggle}
                canToggle={!filtering}
                query={filtering ? query : ''}
                matchIds={filtered.matchIds}
              />
            ))}
          </ul>
        )}
      </section>
    </Commentable>
  );
}

function WbsRow({
  node,
  depth,
  open,
  onToggle,
  canToggle,
  query,
  matchIds,
  parentDeep = false,
}: {
  node: WbsNode;
  depth: number;
  parentDeep?: boolean;
  open: ReadonlySet<string>;
  onToggle: (id: string) => void;
  canToggle: boolean;
  query: string;
  matchIds: ReadonlySet<string>;
}) {
  const Icon = node.icon;
  const isSection = depth === 0;
  const hasChildren = node.children.length > 0;
  const isOpen = hasChildren && open.has(node.id);
  const count = descendantCount(node);
  const matched = matchIds.has(node.id);

  return (
    <li className={cn(isSection && 'mt-1 first:mt-0')}>
      <div
        className={cn(
          'group flex items-center gap-1 rounded-lg pr-2 transition-colors hover:bg-[var(--surface-1)]',
          isSection ? 'min-h-[40px]' : 'min-h-[30px]',
        )}
        style={matched ? { background: 'var(--accent-soft)' } : undefined}
      >
        {hasChildren ? (
          <button
            type="button"
            aria-expanded={isOpen}
            aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${node.code} ${node.label}`}
            disabled={!canToggle}
            onClick={() => onToggle(node.id)}
            className={cn(
              'flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-[var(--surface-3)] disabled:cursor-default disabled:hover:bg-transparent',
              FOCUS_RING,
            )}
            style={{ color: 'var(--ink-tertiary)' }}
          >
            <ChevronRight
              className={cn('h-3.5 w-3.5 transition-transform duration-150', isOpen && 'rotate-90')}
              aria-hidden="true"
            />
          </button>
        ) : (
          <span className="flex h-6 w-6 shrink-0 items-center justify-center" aria-hidden="true">
            <span className="h-1 w-1 rounded-full" style={{ background: 'var(--hairline-strong)' }} />
          </span>
        )}

        <Link
          to={node.to}
          className={cn('flex min-w-0 flex-1 items-center gap-2.5 rounded py-1', FOCUS_RING)}
        >
          {isSection ? (
            <span
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
              style={{
                background: node.deepDive ? 'var(--surface-3)' : 'var(--accent-soft)',
                color: node.deepDive ? 'var(--ink-tertiary)' : 'var(--accent)',
              }}
            >
              <Icon className="h-4 w-4" strokeWidth={1.8} aria-hidden="true" />
            </span>
          ) : (
            <Icon
              className="h-3.5 w-3.5 shrink-0"
              strokeWidth={1.8}
              style={{ color: node.deepDive ? 'var(--ink-tertiary)' : 'var(--accent)' }}
              aria-hidden="true"
            />
          )}

          <span
            className={cn('mono shrink-0 tabular-nums', isSection ? 'text-[11.5px]' : 'text-[10.5px]')}
            style={{ color: 'var(--ink-tertiary)', minWidth: isSection ? 14 : depth === 1 ? 34 : 44 }}
          >
            {node.code}
          </span>

          <span className="flex min-w-0 flex-1 items-baseline gap-2">
            <span
              className={cn(
                'truncate',
                isSection ? 'text-[13.5px] font-semibold tracking-tight' : 'text-[12.5px]',
                !isSection && depth === 1 && 'font-medium',
              )}
              style={{ color: node.deepDive && !isSection ? 'var(--ink-secondary)' : 'var(--ink-primary)' }}
            >
              <Highlighted text={node.label} query={query} />
            </span>
            {node.hint ? (
              <span
                className="hidden truncate text-[11px] @3xl:inline"
                style={{ color: 'var(--ink-tertiary)' }}
              >
                <Highlighted text={node.hint} query={query} />
              </span>
            ) : null}
          </span>
        </Link>

        {/* Tag the boundary only: below a deep-dive row everything is deep
            dive too, and a tag on every step would be noise. */}
        {node.deepDive && !parentDeep ? <DeepDiveTag /> : null}
        {hasChildren ? (
          <span
            className="mono shrink-0 rounded-full px-1.5 py-px text-[10px] tabular-nums"
            style={{ background: 'var(--surface-3)', color: 'var(--ink-tertiary)' }}
            title={`${count} page${count === 1 ? '' : 's'} inside`}
          >
            {count}
          </span>
        ) : null}
      </div>

      {isOpen ? (
        <ul
          className="ml-[11px] border-l pl-2.5"
          style={{ borderColor: 'var(--hairline)' }}
        >
          {node.children.map((child) => (
            <WbsRow
              key={child.id}
              node={child}
              depth={depth + 1}
              open={open}
              onToggle={onToggle}
              canToggle={canToggle}
              query={query}
              matchIds={matchIds}
              parentDeep={node.deepDive}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function Highlighted({ text, query }: { text: string; query: string }) {
  const parts = highlightParts(text, query);
  return (
    <>
      {parts.map((part, i) =>
        part.hit ? (
          <mark
            key={i}
            className="rounded-[3px] px-px"
            style={{ background: 'color-mix(in srgb, var(--accent) 28%, transparent)', color: 'inherit' }}
          >
            {part.text}
          </mark>
        ) : (
          <span key={i}>{part.text}</span>
        ),
      )}
    </>
  );
}

function DeepDiveTag() {
  return (
    <span
      className="shrink-0 rounded-full border border-dashed px-1.5 py-px text-[9.5px] font-medium uppercase tracking-[0.08em]"
      style={{ borderColor: 'var(--hairline-strong)', color: 'var(--ink-tertiary)' }}
    >
      deep dive
    </span>
  );
}
